import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Tile } from "../../panels/Tile/Tile";
import {
  CustomFieldSearchQuery,
  EntityConstants,
  GenericEntity,
  GenericIdEntity,
  ICustomFieldSearch,
  IdTypes,
  IGenericRequestParameters,
  INamedEntity,
  InferSortByOptions,
  ISoftDeletable,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import { useEntityCount, useIdsOnly, useInfiniteListEntity, useUnpaginate } from "../../../api/BaseEntityApi";
import {
  InventoryItem,
  InventoryItemFilters,
  InventoryItemFiltersTranslator,
  inventoryItemsConstants,
  InventoryItemSortingOptionsConsts,
} from "../../../api/Inventories";
import {
  Sample,
  SampleFilters,
  SampleFiltersTranslator,
  samplesConstants,
  SampleSortingOptionsConsts,
} from "../../../api/Samples";
import {
  Dataset,
  DatasetFilters,
  DatasetFiltersTranslator,
  datasetsConstants,
  DatasetSortingOptionsConsts,
} from "../../../api/Datasets";
import {
  Project,
  ProjectFilters,
  ProjectFiltersTranslator,
  projectsConstants,
  ProjectSortingOptionsConsts,
} from "../../../api/Projects";
import {
  Person,
  PersonFilters,
  PersonFiltersTranslator,
  personsConstants,
  PersonSortingOptionsConsts,
} from "../../../api/Person";
import { LucideIcon } from "../../icon/LucideIcon";
import { toUppercase } from "../../helperfunctions/stringFunctions";
import styles from "./styles.module.css";
import { useDebouncedValue } from "../../helperfunctions/useDebouncedValue";
// import { EntityFilterIndicator, IsFilteredDefinition } from "../../tables/EntityFilterIndicator/EntityFilterIndicator";
import { SearchInput } from "../../forms/SearchInput/SearchInput";
import { OrderBySelectDropdown } from "../../buttons/OrderBySelectDropdown/OrderBySelectDropdown";
import { PropertyTranslator } from "../../../api/GenericTranslator";
import { LoadingWrapper } from "../../LoadingWrapper";
import { Link } from "react-router-dom";
import { SessionContext } from "../../contexts/SessionContext";
import { getDetailLink } from "../../../main/Routing";
import ContentRow from "../../tables/ContentRow/ContentRow";
import InfiniteLoader from "react-window-infinite-loader";
import { FixedSizeList } from "react-window";
import { Skeleton } from "../../loaders/Skeleton/Skeleton";
import { Alert } from "../../overlays/Alert/Alert";
import { DateTimeRenderer } from "../../datetime/DateTimeFormatter";
import { NotAvailable, NotSet } from "../../misc/UIconstants";
import { GetPersons, GetProjects } from "../../misc/EntityRenders/EntityRenderer";
import {
  EntitySelectOptions,
  generateAbstractedFromDataType,
} from "../../../Customization/CustomFields/CustomFieldUtils";
import {
  labNotebookEntriesConstants,
  LabNotebookEntry,
  LabNotebookEntryFilters,
  LabNotebookEntryFiltersTranslator,
  LabNotebookEntrySortingOptionsConsts,
} from "../../../ELN/types/LabNotebookEntry";
import {
  LabNotebook,
  LabNotebookFilters,
  LabNotebookFiltersTranslator,
  labNoteBooksConstants,
  LabNotebookSortingOptionsConsts,
} from "../../../ELN/types/LabNotebook";
import { RenderTabStoreHistoryButton } from "../../tables/Tabs/history/TabStoreHistory";
import { filtersToSamplesForm, SampleFilterForm } from "../../../samples/Table/SampleFilterBar";
import {
  InventoryItemFilterForm,
  inventoryItemsFiltersToForm,
} from "../../../Inventories/Table/InventoryItemsFilterBar";
import { DatasetsFilterForm, datasetsFiltersToForm } from "../../../Dataset/Table/DatasetsFilterBar";
import {
  filtersToLabNotebookEntriesForm,
  LabNotebookEntriesFilterForm,
} from "../../../ELN/LabNotebookEntries/Table/LabNotebookEntriesFilterBar";
import {
  LabNotebookFilterForm,
  labNotebooksFiltersToForm,
} from "../../../ELN/LabNotebooks/Table/LabNotebooksFilterBar";
import { filtersToPersonsForm, PersonFilterForm } from "../../../Persons/Table/PersonFilterBar";
import { filtersToProjectsForm, ProjectFilterForm } from "../../../Projects/Table/ProjectFilterBar";
import {
  CustomField,
  customFieldConstants,
  CustomFieldFilters,
  CustomFieldFiltersTranslator,
  CustomFieldSortingOptionsConsts,
} from "../../../api/CustomFields/CustomFields";
import { CustomFieldDataType } from "../../../api/CustomFields/CustomFieldsBase";
import { consolidateCustomFieldFilters } from "../../../Customization/CustomFieldFilters/CustomFieldFilterBuilder/CustomFieldFilterUtils";
import { MultiEditMappedItemObject } from "../../forms/MultiEditForms/common/MultiEditProvider";
import { EntityStateIndicator } from "../../badges/EntityStateIndicator/EntityStateIndicator";
import { CustomTypeLabelRenderer } from "../../../Customization/CustomTypes/generics/CustomTypeRenderer";
import { defaultSamplesFilters } from "../../../samples/Table/SamplesTable";
import { defaultInventoryItemsFilters } from "../../../Inventories/Table/InventoryItemsTable";
import { defaultDatasetsFilters } from "../../../Dataset/Table/DatasetsTable";
import { defaultProjectsFilters } from "../../../Projects/Table/ProjectsTable";
import { defaultPersonsFilters } from "../../../Persons/Table/PersonsTable";
import { defaultLabNotebookEntriesFilters } from "../../../ELN/LabNotebookEntries/Table/LabNotebookEntriesTable";
import { defaultLabNotebooksFilters } from "../../../ELN/LabNotebooks/Table/LabNotebooksTable";
import { DownloadDatasetMulti } from "../../../Dataset/common/DownloadDataset/DownloadDataset";
import {
  CustomFieldFilterForm,
  customFieldFiltersToForm,
} from "../../../Customization/CustomFields/Table/CustomFieldsFilterBar";
import { defaultCustomFieldsFilters } from "../../../Customization/CustomFields/Table/CustomFieldsTable";
import { AbstractedCustomFieldDataTypeIcon } from "../../../Customization/CustomFields/CustomFieldRenderUtils";

type ExtendedEntitySelectOptions = EntitySelectOptions | "Attachment";

const getDataTypesForDataType = (dataType: ExtendedEntitySelectOptions): CustomFieldDataType[] => {
  switch (dataType) {
    case "Sample":
      return ["Sample", "SampleArray"];
    case "InventoryItem":
      return ["InventoryItem", "InventoryItemArray"];
    case "Dataset":
      return ["Dataset", "DatasetArray"];
    case "Project":
      return ["Project", "ProjectArray"];
    case "Person":
      return ["Person", "PersonArray"];
    case "LabNotebook":
      return ["LabNotebook", "LabNotebookArray"];
    case "LabNotebookEntry":
      return ["LabNotebookEntry", "LabNotebookEntryArray"];
    case "LabNotebookExperiment":
      return ["LabNotebookExperiment", "LabNotebookExperimentArray"];
    case "Method":
      return ["Method", "MethodArray"];
    case "SharedContent":
      return ["SharedContent", "SharedContentArray"];
    case "Attachment":
      return ["Dataset"];
  }
};

const generateCustomFieldValuesFilter = (
  id: IdTypes,
  customFields: MultiEditMappedItemObject<CustomField> | undefined
): ICustomFieldSearch => {
  let _temp: CustomFieldSearchQuery[] = [];
  if (customFields && !!Object.keys(customFields).length) {
    Object.entries(customFields).forEach(([cfId, cf]) => {
      _temp.push({
        operator: cf.dataType.includes("Array") ? "IN" : "EQUALS",
        customFieldId: +cfId,
        value: cf.dataType.includes("Array") ? [{ id: id }] : { id: id },
      });
    });
  }
  return consolidateCustomFieldFilters(_temp, "OR", false, true);

  // switch (entityType) {
  //   case "Sample":
  //     return {
  //       filterQuery: {

  //       }
  //       // customFieldValues: [
  //       //   { dataType: "Sample", values: [{ id: id }] },
  //       //   { dataType: "SampleArray", values: [{ id: id }], predicate: "OR" },
  //       // ],
  //     } as ICustomFieldSearch;
  //   case "Inventory":
  //     return {
  //       customFieldValues: [
  //         { dataType: "Inventory", values: [{ id: id }] },
  //         { dataType: "InventoryArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "Dataset":
  //     return {
  //       customFieldValues: [
  //         { dataType: "Dataset", values: [{ id: id }] },
  //         { dataType: "DatasetArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "Project":
  //     return {
  //       customFieldValues: [
  //         { dataType: "Project", values: [{ id: id }] },
  //         { dataType: "ProjectArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "Person":
  //     return {
  //       customFieldValues: [
  //         { dataType: "Person", values: [{ id: id }] },
  //         { dataType: "PersonArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "LabNotebook":
  //     return {
  //       customFieldValues: [
  //         { dataType: "LabNotebook", values: [{ id: id }] },
  //         { dataType: "LabNotebookArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "LabNotebookEntry":
  //     return {
  //       customFieldValues: [
  //         { dataType: "LabNotebookEntry", values: [{ id: id }] },
  //         { dataType: "LabNotebookEntryArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "LabNotebookExperiment":
  //     return {
  //       customFieldValues: [
  //         { dataType: "LabNotebookExperiment", values: [{ id: id }] },
  //         { dataType: "LabNotebookExperimentArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "Method":
  //     return {
  //       customFieldValues: [
  //         { dataType: "Method", values: [{ id: id }] },
  //         { dataType: "MethodArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "SharedContent":
  //     return {
  //       customFieldValues: [
  //         { dataType: "SharedContent", values: [{ id: id }] },
  //         { dataType: "SharedContentArray", values: [{ id: id }], predicate: "OR" },
  //       ],
  //     } as ICustomFieldSearch;
  //   case "ViewableEntity":
  //     return {
  //       customFieldValues: [{ dataType: "ViewableEntity", values: [{ id: id }] }],
  //     } as ICustomFieldSearch;
  // }
};

interface RenderCustomFieldReferencesProps<
  Entity extends GenericEntity & Partial<ISoftDeletable> & INamedEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
  FilterForm extends StringIndexedDict,
> {
  defaultFilters: Filters;
  entityConstants: EntityConstants;
  filterToForm: (filters: Filters) => FilterForm;
  translatorConsts: PropertyTranslator<Filters>;
  orderByOptions: readonly InferSortByOptions<Filters>[];
  count: number | undefined;
  rowLabel?: (entity: Entity) => React.ReactNode;
  rowDate?: (entity: Entity) => React.ReactNode;
  rowInfo?: (entity: Entity) => React.ReactNode;
  rowTitle?: (entity: Entity) => string;
  rowControls?: (entity: Entity) => React.ReactNode;
  rowId?: (entity: Entity) => React.ReactNode;
  onRowClick?: (entity: Entity) => void;
  controls?: React.ReactNode;
}
const RenderCustomFieldReferences = <
  Entity extends GenericEntity & Partial<ISoftDeletable> & INamedEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
  FilterForm extends StringIndexedDict,
>({
  defaultFilters,
  entityConstants,
  filterToForm,
  translatorConsts,
  orderByOptions,
  count,
  rowLabel,
  rowDate,
  rowInfo,
  rowTitle,
  rowControls,
  rowId,
  onRowClick,
  controls,
}: RenderCustomFieldReferencesProps<Entity, Filters, FilterForm>) => {
  const { route } = useContext(SessionContext);
  const [show, setShow] = useState(!!count);
  const [filters, setFilters] = useState<Filters>({} as Filters);
  const activeFilters: Filters = useMemo(() => defaultFilters, [defaultFilters]);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebouncedValue(searchValue, 500);
  const [items, setItems] = useState<Entity[]>([]);
  const pageSize = 10;

  useEffect(() => {
    if (activeFilters) setFilters(activeFilters);
  }, [activeFilters]);

  const { data, error, fetchNextPage, hasNextPage, isFetching, status, fetchStatus } = useInfiniteListEntity<Entity>(
    entityConstants.resource,
    {
      page: 1,
      pageSize: pageSize,
      includeCount: false,
      ...{ searchTerm: debouncedSearchValue },
      ...filters,
    },
    { enabled: true },
    "post"
  );

  useEffect(() => {
    if (data) {
      setItems(data.pages.map((d) => d.results).flat());
    }
  }, [data]);

  const loadMoreCallback = useCallback(() => {}, []);
  const itemCount = hasNextPage ? items.length + 1 : items.length;
  const loadMoreItems = isFetching ? loadMoreCallback : hasNextPage ? fetchNextPage : loadMoreCallback;

  const isItemLoaded = useCallback(
    (index: number) => !hasNextPage || index < items.length - pageSize,
    [hasNextPage, items.length, pageSize]
  );

  const Item = useCallback(
    ({ index, style }: { index: number; style: CSSProperties }) => {
      if (!isItemLoaded(index))
        return (
          <div style={{ ...style, paddingBottom: "5px" }} key={index}>
            <span className="skeleton-block btn-lg" style={{ height: 51 }} />
          </div>
        );

      const entity = items[index];
      return (
        <div style={{ ...style, paddingBottom: "5px" }} key={index} title={rowTitle?.(entity)}>
          <Link
            key={index}
            to={route(getDetailLink(entityConstants.frontendIndexRoute, entity.id))}
            style={{ textDecoration: "none" }}
            target={"_blank"}
          >
            <ContentRow
              key={index}
              icon={
                <div className={styles.icon}>
                  <LucideIcon name={entityConstants.icon} />
                </div>
              }
              label={rowLabel?.(entity)}
              currentIndicator={
                <>
                  {!!entity.isDeleted ? (
                    <div>
                      <label className={"label label-soft-warning"} title="trashed">
                        <LucideIcon name="trash" />
                      </label>
                    </div>
                  ) : (
                    ""
                  )}
                </>
              }
              date={rowDate?.(entity)}
              id={
                rowId ? (
                  rowId?.(entity)
                ) : (
                  <>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id ?? NotAvailable}
                  </>
                )
              }
              info={rowInfo?.(entity)}
              controls={rowControls?.(entity)}
              onClick={onRowClick ? () => onRowClick(entity) : undefined}
            />
          </Link>
        </div>
      );
    },
    [
      entityConstants.entitySingular,
      entityConstants.frontendIndexRoute,
      entityConstants.icon,
      isItemLoaded,
      items,
      onRowClick,
      route,
      rowControls,
      rowDate,
      rowId,
      rowInfo,
      rowLabel,
      rowTitle,
    ]
  );

  return (
    <div className={styles.customFieldReferencesContainerMain}>
      <div
        className={`${styles.customFieldReferencesContainer} ${
          show ? styles.customFieldReferencesContainerActive : ""
        } ${!count ? styles.customFieldReferencesContainerDisabled : ""}
          
        `}
        onClick={() => !!count && setShow((prev) => !prev)}
        style={{ cursor: !!count ? "pointer" : "default" }}
      >
        <>{!!count && <>{show ? <LucideIcon name="chevron-down" /> : <LucideIcon name="chevron-right" />}</>}</>
        <LucideIcon name={entityConstants.icon} style={{ color: "var(--primary)" }} />{" "}
        {toUppercase(entityConstants.entityPlural)}
        <span className="badge">{count ?? 0}</span>
        <div className="flex row-nowrap align-center gap-5" style={{ marginLeft: "auto" }}>
          {controls ?? <></>}
          <RenderTabStoreHistoryButton
            entityConstants={entityConstants}
            filters={filters}
            filtersToForm={filterToForm}
          />
        </div>
      </div>
      {show && (
        <div className={styles.customFieldReferencesContainerContent}>
          <div
            className="flex col-nowrap align-center gap-5"
            style={{ width: "100%", height: "100%", overflow: "hidden" }}
          >
            <div className="flex row-nowrap align-center gap-5" style={{ width: "100%", padding: "3px 0px 0px 3px" }}>
              {/* <EntityFilterIndicator<Entity, Filters>
                filters={filters}
                translatorConsts={translatorConsts}
                excludeFilters={
                  {
                    excludeIds: () => false,
                    customFieldValues: () => false,
                    orderBy: () => false,
                  } as Partial<IsFilteredDefinition<Filters>>
                }
                overlayPlaycement="right"
              /> */}

              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} />
              <div style={{ marginLeft: "auto" }}>
                <OrderBySelectDropdown
                  btnCls="btn btn-default"
                  orderByOptions={orderByOptions}
                  currentFilters={filters}
                  setCurrentFilters={setFilters}
                />
              </div>
            </div>
            <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error} type="entity-tile">
              <div className={isFetching ? `${styles.container} ${styles.container_loading}` : styles.container}>
                {items && items.length > 0 ? (
                  <InfiniteLoader
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems as any}
                  >
                    {({ onItemsRendered, ref }) => (
                      <FixedSizeList
                        itemCount={itemCount}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        width="100%"
                        height={items.length > pageSize ? pageSize * 59 : items.length * 59}
                        itemSize={59}
                      >
                        {Item}
                      </FixedSizeList>
                    )}
                  </InfiniteLoader>
                ) : (
                  <>
                    {isFetching ? (
                      <Skeleton type="rows" />
                    ) : (
                      <>
                        {debouncedSearchValue ? (
                          <Alert type="light" message={`No results for: "${debouncedSearchValue}"`} fit centered />
                        ) : (
                          <Alert type="light" message={`No related ${entityConstants.entityPlural}`} fit centered />
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </LoadingWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

interface CustomFieldReferencesTileProps<Entity extends GenericIdEntity> {
  entity: Entity;
  entityType: ExtendedEntitySelectOptions;
  disabled?: boolean;
  labNotebookFilters?: LabNotebookFilters;
  labNotebookEntryFilters?: LabNotebookEntryFilters;
  onEmptyCallback?: () => void;
}
export const CustomFieldReferencesTile = <Entity extends GenericIdEntity>({
  entity,
  entityType,
  disabled = false,
  labNotebookFilters = {},
  labNotebookEntryFilters = {},
  onEmptyCallback,
}: CustomFieldReferencesTileProps<Entity>) => {
  const { api } = useContext(SessionContext);

  const { data: customFields } = useUnpaginate<CustomField, CustomFieldFilters>(customFieldConstants.resource, {
    dataTypes: getDataTypesForDataType(entityType),
  });

  const customFieldValuesFilter = generateCustomFieldValuesFilter(entity.id, customFields);

  const { data: samplesCount, isFetching: isFetchingSamples } = useEntityCount<SampleFilters>(
    samplesConstants.resource,
    customFieldValuesFilter,
    {
      enabled: !disabled && !!customFieldValuesFilter.filterQuery,
    }
  );
  const { data: inventoriesCount, isFetching: isFetchingInventories } = useEntityCount<InventoryItemFilters>(
    inventoryItemsConstants.resource,
    customFieldValuesFilter,
    { enabled: !disabled && !!customFieldValuesFilter.filterQuery }
  );

  const { data: datasetIds, isFetching: isFetchingDatasets } = useIdsOnly<Dataset["id"], DatasetFilters>(
    datasetsConstants.resource,
    { ...customFieldValuesFilter, includeCount: true },
    {
      enabled: !disabled && !!customFieldValuesFilter.filterQuery,
    }
  );

  const { data: projectsCount, isFetching: isFetchingProjects } = useEntityCount<ProjectFilters>(
    projectsConstants.resource,
    customFieldValuesFilter,
    {
      enabled: !disabled && !!customFieldValuesFilter.filterQuery,
    }
  );
  const { data: personsCount, isFetching: isFetchingPersons } = useEntityCount<PersonFilters>(
    personsConstants.resource,
    customFieldValuesFilter,
    {
      enabled: !disabled && !!customFieldValuesFilter.filterQuery,
    }
  );
  const { data: labNotebookEntriesCount, isFetching: isFetchingLabNotebookEntries } =
    useEntityCount<LabNotebookEntryFilters>(labNotebookEntriesConstants.resource, labNotebookEntryFilters, {
      enabled: !disabled && !!Object.keys(labNotebookEntryFilters).length,
    });

  const { data: labNoteBooksCount, isFetching: isFetchingLabNoteBooks } = useEntityCount<LabNotebookFilters>(
    labNoteBooksConstants.resource,
    labNotebookFilters,
    { enabled: !disabled && !!Object.keys(labNotebookFilters).length }
  );

  const { data: customFieldsCount, isFetching: isFetchingcustomFields } = useEntityCount<CustomFieldFilters>(
    customFieldConstants.resource,
    customFieldValuesFilter,
    {
      enabled: !disabled && !!customFieldValuesFilter.filterQuery,
    }
  );

  const resultsCount =
    (samplesCount?.count ?? 0) +
    (inventoriesCount?.count ?? 0) +
    (datasetIds?.count ?? 0) +
    (projectsCount?.count ?? 0) +
    (personsCount?.count ?? 0) +
    (labNotebookEntriesCount?.count ?? 0) +
    (labNoteBooksCount?.count ?? 0) +
    (customFieldsCount?.count ?? 0);

  const isFetching =
    isFetchingSamples ||
    isFetchingInventories ||
    isFetchingDatasets ||
    isFetchingProjects ||
    isFetchingPersons ||
    isFetchingLabNotebookEntries ||
    isFetchingLabNoteBooks ||
    isFetchingcustomFields;

  useEffect(() => {
    if (!resultsCount && !isFetching) {
      onEmptyCallback?.();
    }
  }, [isFetching, onEmptyCallback, resultsCount]);

  return (
    <Tile
      isFolded={!resultsCount ? true : false}
      foldable={!resultsCount ? false : true}
      disabled={!resultsCount ? true : false}
      resizable
    >
      <Tile.Head
        title={
          <div className="flex row-nowrap align-center gap-5">
            <LucideIcon name={"link"} /> Referenced by
            <span className={`badge ${isFetching && !disabled ? "loading" : ""}`}>
              {disabled ? "" : isFetching ? "•" : typeof resultsCount === "number" ? resultsCount : "•"}
            </span>
          </div>
        }
      />

      <Tile.Body style={{ overflowX: "hidden" }}>
        <div className="flex col-nowrap gap-5" style={{ width: "100%", alignItems: "flex-start" }}>
          {!!samplesCount?.count && (
            <RenderCustomFieldReferences<Sample, SampleFilters, SampleFilterForm>
              entityConstants={samplesConstants}
              filterToForm={filtersToSamplesForm}
              orderByOptions={SampleSortingOptionsConsts}
              translatorConsts={SampleFiltersTranslator}
              count={samplesCount?.count}
              defaultFilters={{ ...customFieldValuesFilter, ...defaultSamplesFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={samplesConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <>
                  {e.enteredOn ? (
                    <DateTimeRenderer date={e.enteredOn} includeElapsed={false} includeTime={false} />
                  ) : (
                    NotSet
                  )}
                </>
              )}
              rowInfo={(e) => <>{e.enteredBy ? <GetPersons persons={e.enteredBy} createLinks={false} /> : NotSet}</>}
              rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
            />
          )}
          {!!inventoriesCount?.count && (
            <RenderCustomFieldReferences<InventoryItem, InventoryItemFilters, InventoryItemFilterForm>
              entityConstants={inventoryItemsConstants}
              filterToForm={inventoryItemsFiltersToForm}
              orderByOptions={InventoryItemSortingOptionsConsts}
              translatorConsts={InventoryItemFiltersTranslator}
              count={inventoriesCount?.count}
              defaultFilters={{ ...customFieldValuesFilter, ...defaultInventoryItemsFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={inventoryItemsConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <>
                  {e.enteredOn ? (
                    <DateTimeRenderer date={e.enteredOn} includeElapsed={false} includeTime={false} />
                  ) : (
                    NotSet
                  )}
                </>
              )}
              rowInfo={(e) => <>{e.enteredBy ? <GetPersons persons={e.enteredBy} createLinks={false} /> : NotSet}</>}
              rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
            />
          )}
          {!!datasetIds?.count && (
            <RenderCustomFieldReferences<Dataset, DatasetFilters, DatasetsFilterForm>
              entityConstants={datasetsConstants}
              filterToForm={datasetsFiltersToForm}
              orderByOptions={DatasetSortingOptionsConsts}
              translatorConsts={DatasetFiltersTranslator}
              count={datasetIds?.count}
              defaultFilters={{ ...customFieldValuesFilter, ...defaultDatasetsFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={datasetsConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <>
                  {e.creationDate ? (
                    <DateTimeRenderer date={e.creationDate} includeElapsed={false} includeTime={false} />
                  ) : (
                    NotSet
                  )}
                </>
              )}
              rowInfo={(e) => <>{e.enteredBy ? <GetPersons persons={e.enteredBy} createLinks={false} /> : NotSet}</>}
              rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
              controls={
                <DownloadDatasetMulti
                  btnStyle="btn btn-xs btn-primary"
                  api={api}
                  filters={{ ids: datasetIds.results }}
                  title="Download all datasets"
                />
              }
            />
          )}
          {!!projectsCount?.count && (
            <RenderCustomFieldReferences<Project, ProjectFilters, ProjectFilterForm>
              entityConstants={projectsConstants}
              filterToForm={filtersToProjectsForm}
              orderByOptions={ProjectSortingOptionsConsts}
              translatorConsts={ProjectFiltersTranslator}
              count={projectsCount?.count}
              defaultFilters={{ ...customFieldValuesFilter, ...defaultProjectsFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={projectsConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <>
                  {e.enteredOn ? (
                    <DateTimeRenderer date={e.enteredOn} includeElapsed={false} includeTime={false} />
                  ) : (
                    NotSet
                  )}
                </>
              )}
              rowInfo={(e) => <>{e.enteredBy ? <GetPersons persons={e.enteredBy} createLinks={false} /> : NotSet}</>}
              rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
            />
          )}
          {!!personsCount?.count && (
            <RenderCustomFieldReferences<Person, PersonFilters, PersonFilterForm>
              entityConstants={personsConstants}
              filterToForm={filtersToPersonsForm}
              orderByOptions={PersonSortingOptionsConsts}
              translatorConsts={PersonFiltersTranslator}
              count={personsCount?.count}
              defaultFilters={{ ...customFieldValuesFilter, ...defaultPersonsFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={personsConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => <DateTimeRenderer date={e.enteredOn} includeElapsed={false} includeTime={false} />}
              rowInfo={(e) => <>{e.email || NotSet}</>}
              rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
            />
          )}
          {!!labNotebookEntriesCount?.count && labNotebookEntryFilters && (
            <RenderCustomFieldReferences<LabNotebookEntry, LabNotebookEntryFilters, LabNotebookEntriesFilterForm>
              entityConstants={labNotebookEntriesConstants}
              filterToForm={filtersToLabNotebookEntriesForm}
              orderByOptions={LabNotebookEntrySortingOptionsConsts}
              translatorConsts={LabNotebookEntryFiltersTranslator}
              count={labNotebookEntriesCount?.count}
              defaultFilters={{ ...labNotebookEntryFilters, ...defaultLabNotebookEntriesFilters }}
              // rowLabel={(e) =>
              //   e.name ?? <DateTimeRenderer date={e.entryDate} includeElapsed={false} includeTime={false} />
              // }
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>
                    {e.name ?? <DateTimeRenderer date={e.entryDate} includeElapsed={false} includeTime={false} />}
                  </span>
                  <EntityStateIndicator entityConstants={labNotebookEntriesConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <LucideIcon name="save" style={{ marginBottom: "2px", width: "12px" }} />
                  <DateTimeRenderer date={e.modifiedOn} includeElapsed={true} includeTime={false} />
                </div>
              )}
              rowInfo={(e) => <>{`${e.labNotebook.name} > ${e.labNotebookExperiment.name}`}</>}
            />
          )}
          {!!labNoteBooksCount?.count && labNotebookFilters && (
            <RenderCustomFieldReferences<LabNotebook, LabNotebookFilters, LabNotebookFilterForm>
              entityConstants={labNoteBooksConstants}
              filterToForm={labNotebooksFiltersToForm}
              orderByOptions={LabNotebookSortingOptionsConsts}
              translatorConsts={LabNotebookFiltersTranslator}
              count={labNoteBooksCount?.count}
              defaultFilters={{ ...labNotebookFilters, ...defaultLabNotebooksFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={labNoteBooksConstants} entity={e} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <LucideIcon name="save" style={{ marginBottom: "2px", width: "12px" }} />
                  <DateTimeRenderer date={e.modifiedOn} includeElapsed={true} includeTime={false} />
                </div>
              )}
              rowInfo={(e) => <GetProjects projects={e.projects} />}
            />
          )}
          {!!customFieldsCount?.count && customFieldValuesFilter && (
            <RenderCustomFieldReferences<CustomField, CustomFieldFilters, CustomFieldFilterForm>
              entityConstants={customFieldConstants}
              filterToForm={customFieldFiltersToForm}
              orderByOptions={CustomFieldSortingOptionsConsts}
              translatorConsts={CustomFieldFiltersTranslator}
              count={customFieldsCount?.count}
              defaultFilters={{ ...customFieldValuesFilter, ...defaultCustomFieldsFilters }}
              rowLabel={(e) => (
                <div className="ellipsisContainer">
                  <span>{e.name || NotAvailable}</span>
                  <EntityStateIndicator entityConstants={customFieldConstants} entity={e as any} hideLabels />
                </div>
              )}
              rowDate={(e) => (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <LucideIcon name="save" style={{ marginBottom: "2px", width: "12px" }} />
                  <DateTimeRenderer date={e.modifiedOn} includeElapsed={true} includeTime={false} />
                </div>
              )}
              rowInfo={(e) => {
                const dataType = generateAbstractedFromDataType(e.dataType);
                return (
                  <div className="flex row-nowrap align-center gap-5">
                    <AbstractedCustomFieldDataTypeIcon
                      dataType={generateAbstractedFromDataType(e?.dataType).abstractedDataType!}
                    />
                    {dataType.abstractedDataType}
                    {dataType.isMulti && (
                      <label className="label label-soft-success" style={{ margin: 0 }}>
                        Multi
                      </label>
                    )}
                  </div>
                );
              }}
            />
          )}
        </div>
      </Tile.Body>
    </Tile>
  );
};
